<template>
    <div v-if="page" class="handbook-content" ref="handbookContent" id="handbook-content">
        <div class="handbook-timetravel" v-if="currentVersion !== defaultVersion">
            <ProIcon icon="time-travel" />
            <div>
                <span class="handbook-timetravel__message handbook-timetravel__message--xs">{{ $t("handbook.timetravel_older_version_mobile") }}</span>
                <span class="handbook-timetravel__message handbook-timetravel__message--sm">{{ $t("handbook.timetravel_older_version_desktop") }}<br /></span>
                <i18n path="handbook.timetravel_redirect_mobile" tag="span" class="handbook-timetravel__message handbook-timetravel__message--xs">
                    <template v-slot:redirect_link>
                        <ProLink
                            :to="{
                                name: 'handbook-product-all',
                                params: { product: currentProduct.slug, pathMatch: versionlessPath },
                            }"
                            >{{ $t("handbook.timetravel_redirect_link_mobile") }}</ProLink
                        >
                    </template>
                </i18n>
                <i18n path="handbook.timetravel_redirect_desktop" tag="span" class="handbook-timetravel__message handbook-timetravel__message--sm">
                    <template v-slot:redirect_link>
                        <ProLink
                            :to="{
                                name: 'handbook-product-all',
                                params: { product: currentProduct.slug, pathMatch: versionlessPath },
                            }"
                            >{{ $t("handbook.timetravel_redirect_link_desktop") }}</ProLink
                        >
                    </template>
                </i18n>
            </div>
        </div>

        <div class="handbook-content-header">
            <nav v-if="page.parent" class="handbook-content-header__breadcrumbs" aria-label="Breadcrumbs">
                <ProLink :to="{ name: 'handbook-product-all', params: { product: currentProduct.slug, pathMatch: requiredVersion + `${page.parent.slug}` } }">
                    <span class="crumb-title">{{ page.parent.version.title }}</span>
                    <ProIcon icon="arrow-right" />
                </ProLink>
            </nav>
            <h1 class="handbook-content-header__title text-heading text-heading">{{ page.version.title }}</h1>
            <p class="handbook-content-header__description text-body">{{ page.version.description }}</p>
        </div>

        <template v-if="isOverview">
            <div class="handbook-overview-section" v-for="subPage in page.descendants" :key="subPage.id">
                <h2 class="handbook-overview-section__title text-heading">{{ subPage.version.title }}</h2>
                <p class="handbook-overview-section__description text-body" v-if="subPage.version.description">{{ subPage.version.description }}</p>
                <ProLink
                    :to="{
                        name: 'handbook-product-all',
                        params: { product: currentProduct.slug, pathMatch: requiredVersion + `${page.slug}/${subPage.slug}` },
                    }"
                    :aria-label="`${subPage.version.title}: ${$t('global.see_section')}`"
                    >{{ $t("global.see_section") }}<ProIcon icon="cta-right"
                /></ProLink>
            </div>
        </template>

        <template v-else>
            <div class="handbook-content-sections">
                <PageSection v-for="section in page.version.data" :key="section.id" :id="section.id" :segment-data="section.data"></PageSection>
            </div>
        </template>

        <div class="handbook-pagination" v-if="previousPage || nextPage">
            <ProButton
                v-if="previousPage"
                pill
                large
                variant="alt"
                class="handbook-pagination__button handbook-pagination__button--previous"
                :to="{ name: 'handbook-product-all', params: { product: currentProduct.slug, pathMatch: requiredVersion + `${previousPage.path}` } }"
                :aria-label="`${$t('handbook.prev_page')} ${previousPage.version.title}`"
                :title="`${$t('handbook.prev_page')} ${previousPage.version.title}`"
            >
                <ProIcon icon="arrow-long-left" />
                <span>{{ previousPage.version.title }}</span>
            </ProButton>
            <div v-else></div>

            <ProButton
                v-if="nextPage"
                pill
                large
                variant="primary"
                class="handbook-pagination__button handbook-pagination__button--next"
                :to="{ name: 'handbook-product-all', params: { product: currentProduct.slug, pathMatch: requiredVersion + `${nextPage.path}` } }"
                :aria-label="`${$t('handbook.next_page')} ${nextPage.version.title}`"
                :title="`${$t('handbook.next_page')} ${nextPage.version.title}`"
            >
                <span>{{ nextPage.version.title }}</span>
                <ProIcon icon="arrow-long-right" />
            </ProButton>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { metaTitles, metaDescriptions, metaImages } from "~/utils/meta";
import { slugify } from "@/utils";
import PageSection from "@/components/handbook/segments/Section";

export default {
    components: {
        PageSection,
    },

    scrollToTop: true,

    computed: {
        ...mapGetters({
            defaultVersion: "handbook/getDefaultVersion",
            currentVersion: "handbook/getCurrentVersion",
            currentProduct: "handbook/getCurrentProduct",
            page: "handbook/getPage",
            previousPage: "handbook/getPreviousPage",
            isSearching: "handbook/getIsSearching",
            nextPage: "handbook/getNextPage",
        }),

        isOverview() {
            return this.page.descendants.length;
        },

        versionlessPath() {
            const basePathArray = this.$route.params.pathMatch.split("/");
            basePathArray.shift();
            return basePathArray.join("/").slice(0, -1);
        },

        requiredVersion() {
            if (this.currentVersion !== this.defaultVersion) {
                return this.currentVersion.slug + "/";
            }

            return "";
        },
    },

    async asyncData({ store, route, error, redirect }) {
        let version = route.params.pathMatch.split("/")[0];
        let realPath = route.params.pathMatch;

        if (/^\d+(\.?\d*)*$/g.test(version)) {
            if (!store.getters["handbook/getVersion"](version)) {
                // If version matches semantic versioning format (5.1.x etc) but isn't found, show 404
                return error({ status: 404 });
            }

            const pathArray = route.params.pathMatch.split("/");
            pathArray.shift();
            realPath = pathArray.join("/");
        }

        let page;

        try {
            page = await store.dispatch("handbook/fetchPage", {
                path: realPath,
            });
        } catch (e) {
            if (e.response?.status === 404) {
                return error({ statusCode: 404 });
            }

            throw e;
        }

        /*
            Rewrite URL to new section ID style if a section query parameter is present.
            Converts `/foo?section=1234` to `/foo#baa-1234`.
        */
        if (route.query.section) {
            let section = page.version.data.find((segment) => segment.type === "section" && segment.id == route.query.section);
            if (section) {
                redirect({
                    path: route.path,
                    hash: section.data.title ? slugify(section.data.title) + "-" + section.id : section.id,
                });
            }
        }
    },

    watch: {
        "$i18n.locale"() {
            this.$store.dispatch("handbook/fetchPage", {
                path: this.$route.params.pathMatch,
            });
        },
    },

    watchQuery: ["v"],

    head() {
        if (this.page) {
            const meta = this.$t(`meta.${this.currentProduct.slug == "procreate" ? "handbook" : "handbook_pocket"}`);
            const title = `${this.page.version.title} - ${meta.title}`;

            const currentLocale = () => {
                return this.$i18n.locales.find((l) => l.code == this.$i18n.locale);
            };

            return {
                title: title,
                meta: [
                    ...metaTitles(title),
                    ...metaDescriptions(this.page.version.description ?? meta.description),
                    ...(this.currentVersion.dynamic_meta
                        ? metaImages(process.env.ADMIN_API_URL + `/page/meta-image/${this.page.id}?lang=${currentLocale().iso}`)
                        : metaImages(this.$asset(meta.image))),
                ],
            };
        }
    },
};
</script>
